
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type UserRole = 'admin' | 'user' | 'atc' | 'dm' | 'sales_assistant';
type BaseAssignment = {
  id: string;
  role: UserRole;
  base_name: string;
};

export const useBaseAssignments = (selectedRole: UserRole | '') => {
  const [bases, setBases] = useState<{ id: number; name: string }[]>([]);
  const [assignedBases, setAssignedBases] = useState<BaseAssignment[]>([]);
  const [selectedBase, setSelectedBase] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch all available bases
  const fetchBases = async () => {
    try {
      const { data, error } = await supabase
        .from('bases')
        .select('id, name')
        .order('name');
        
      if (error) throw error;
      setBases(data || []);
    } catch (error: any) {
      console.error("Error fetching bases:", error);
      toast.error("Failed to load bases: " + error.message);
    }
  };

  // Fetch assigned bases for the selected role
  const fetchAssignedBases = async () => {
    if (!selectedRole) {
      setAssignedBases([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Direct table query
      const { data, error } = await supabase
        .from('role_base_assignments')
        .select('id, role, base_name')
        .eq('role', selectedRole);
      
      if (error) {
        console.error("Error fetching role base assignments:", error);
        toast.error("Failed to load role base assignments: " + error.message);
        setAssignedBases([]);
      } else {
        setAssignedBases(data || []);
      }
    } catch (error: any) {
      console.error(`Error fetching bases for role ${selectedRole}:`, error);
      toast.error("Failed to load role base assignments");
    } finally {
      setLoading(false);
    }
  };

  // Assign a base to the selected role
  const assignBaseToRole = async () => {
    if (!selectedRole || !selectedBase) {
      toast.error("Please select both a role and a base");
      return;
    }
    
    try {
      setSaving(true);
      
      // Check if this assignment already exists
      const { data: existingCheck, error: checkError } = await supabase
        .from('role_base_assignments')
        .select('id')
        .eq('role', selectedRole)
        .eq('base_name', selectedBase)
        .limit(1);
        
      if (checkError) {
        throw checkError;
      }
      
      if (existingCheck && existingCheck.length > 0) {
        toast.info("This base is already assigned to this role");
        return;
      }
      
      // Direct insert into role_base_assignments table
      const { error } = await supabase
        .from('role_base_assignments')
        .insert([{ 
          role: selectedRole, 
          base_name: selectedBase
        }]);
        
      if (error) {
        console.error("Insertion error:", error);
        
        // Handle specific error cases
        if (error.code === '23505') { // Unique violation
          toast.error("This base is already assigned to this role");
        } else if (error.code === '42501') { // Permission denied
          toast.error("You don't have permission to assign bases. Please verify your admin status.");
        } else {
          toast.error("Failed to assign base: " + error.message);
        }
        return;
      }
      
      toast.success(`Base "${selectedBase}" assigned to role successfully`);
      setSelectedBase(""); // Reset selection
      fetchAssignedBases(); // Refresh the list
    } catch (error: any) {
      console.error("Error assigning base:", error);
      toast.error("Failed to assign base: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Remove a base assignment
  const removeBaseAssignment = async (assignmentId: string) => {
    try {
      // Direct delete from role_base_assignments table
      const { error } = await supabase
        .from('role_base_assignments')
        .delete()
        .eq('id', assignmentId);
        
      if (error) {
        console.error("Deletion error:", error);
        
        // Handle specific error cases
        if (error.code === '42501') { // Permission denied
          toast.error("You don't have permission to remove base assignments. Please verify your admin status.");
        } else {
          toast.error("Failed to remove base assignment: " + error.message);
        }
        return;
      }
      
      toast.success("Base assignment removed");
      fetchAssignedBases(); // Refresh the list
    } catch (error: any) {
      console.error("Error removing base assignment:", error);
      toast.error("Failed to remove base assignment: " + error.message);
    }
  };

  // Initialize data
  useEffect(() => {
    fetchBases();
  }, []);

  // When selected role changes, fetch its assigned bases
  useEffect(() => {
    fetchAssignedBases();
  }, [selectedRole]);

  return {
    bases,
    assignedBases,
    selectedBase,
    setSelectedBase,
    loading,
    saving,
    assignBaseToRole,
    removeBaseAssignment
  };
};
